import styled from 'styled-components'

import { AnimatedItem } from '@/components/ui/animated-item'
import { Card as UiCard } from '@/components/ui/card'
import { Tooltip } from '@/components/ui/tooltip'

import { SELECTED_ROW_SELECTOR } from './linked-columns.attributes'
import {
  COLUMN_HEADER_MIN_HEIGHT,
  LINK_COLOR_VAR,
  LINK_STROKE_WIDTH,
} from './linked-columns.constants'

export const Column = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['12'],
  height: '100%',
  minHeight: 0,
  minWidth: 0,
}))

export const Header = styled.div(({ theme }) => ({
  '& [data-component-id="Button"]': {
    whiteSpace: 'nowrap',
  },
  '& > [data-component-id="Tooltip"]': {
    flexShrink: 0,
  },
  // Note: the title takes the free width and truncates; the add button never shrinks or wraps
  '& > h2': {
    flex: 1,
    minWidth: 0,
  },
  alignItems: 'center',
  display: 'flex',
  gap: theme.space['8'],
  justifyContent: 'space-between',
  // Note: the height of the small add button, so a column without one lines up with its neighbours
  minHeight: COLUMN_HEADER_MIN_HEIGHT,
  minWidth: 0,
}))

export const Panel = styled(UiCard)<{ $dimmed: boolean }>(({ theme, $dimmed }) => ({
  border: `${theme.border.size['1']} solid ${theme.colors.gray500}`,
  borderRadius: theme.border.radius['12'],
  boxShadow: 'none',
  display: 'flex',
  filter: $dimmed ? 'blur(1px)' : 'none',
  flex: 1,
  flexDirection: 'column',
  height: 'auto',
  minHeight: 0,
  opacity: $dimmed ? 0.6 : 1,
  overflow: 'hidden',
  // Note: the list's scrollbar sits on this edge, so 4px here puts it 4px from the border
  padding: theme.space['4'],
  pointerEvents: $dimmed ? 'none' : 'auto',
  transition: `filter ${theme.transition.duration.md}, opacity ${theme.transition.duration.md}`,
}))

// Note: the thumb uses the selected row's background (gray200) so the scrollbar matches the active item
export const List = styled.div(({ theme }) => ({
  // Note: the selected row stays pinned to the top or bottom edge while it is scrolled out of view,
  // so the parent of the next column (and the connector) is always visible
  [`> *:has(${SELECTED_ROW_SELECTOR})`]: {
    // Note: 0, not the list padding - sticky offsets start at the content edge, which is already inside it
    bottom: 0,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  '&::-webkit-scrollbar': { width: theme.space['8'] },
  '&::-webkit-scrollbar-button': { display: 'none' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.colors.gray200,
    borderRadius: theme.border.radius['4'],
  },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: theme.space['8'],
  minHeight: 0,
  overflowY: 'auto',
  // Note: 12px + the panel's 4px keeps the rows 16px from the border
  padding: theme.space['12'],
  scrollbarColor: `${theme.colors.gray200} transparent`,
  scrollbarWidth: 'thin',
}))

export const ListItem = styled(AnimatedItem)(() => ({
  flexShrink: 0,
}))

// Note: the selected row follows the connector color (set by LinkedColumns), or the neutral gray without one
export const Row = styled.div<{ $selected: boolean }>(({ theme, $selected }) => {
  const selectedColor = `var(${LINK_COLOR_VAR}, ${theme.colors.gray600})`

  return {
    '&:hover': {
      borderColor: $selected ? selectedColor : theme.colors.gray600,
    },
    alignItems: 'center',
    backgroundColor: $selected ? theme.colors.gray200 : theme.colors.white,
    border: `${theme.border.size['1']} solid ${$selected ? selectedColor : theme.colors.gray400}`,
    borderRadius: theme.border.radius['8'],
    // Note: an inset shadow tops the 1px border up to the connector's stroke width without moving the layout
    boxShadow: $selected ? `inset 0 0 0 ${LINK_STROKE_WIDTH - 1}px ${selectedColor}` : 'none',
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    gap: theme.space['12'],
    padding: `${theme.space['12']} ${theme.space['16']}`,
    transition: ['background-color', 'border-color', 'box-shadow']
      .map((property) => `${property} ${theme.transition.duration.md}`)
      .join(', '),
    width: '100%',
  }
})

export const DragHandle = styled.span(({ theme }) => ({
  alignItems: 'center',
  color: theme.colors.black300,
  cursor: 'grab',
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'center',
  touchAction: 'none',
  width: '2rem',
}))

export const SkeletonRow = styled(Row)(() => ({
  pointerEvents: 'none',
}))

export const SkeletonDragHandle = styled(DragHandle)(() => ({
  cursor: 'default',
}))

// Note: lets the title shrink and truncate inside the row instead of widening it
export const RowTitleTooltip = styled(Tooltip)(() => ({
  minWidth: 0,
}))

export const Subtitle = styled.span(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.space['4'],
  whiteSpace: 'nowrap',
}))

export const RowMain = styled.button(({ theme }) => ({
  alignItems: 'stretch',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: theme.space['2'],
  minWidth: 0,
  padding: 0,
  textAlign: 'left',
}))

export const RowActions = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  gap: theme.space['2'],
}))

export const EmptyWrapper = styled.div(() => ({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
}))

export const Skeletons = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['8'],
}))
